import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { type EnqueueSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import STATUS_CODES from '@/constants/statuscode.constants';
import useHandleError from '@/hooks/useHandleError';
import { customEventAtom } from '@/pages/Administration/Devices';
import devicesAtom, {
  CustomEventTypes,
  selectedDeviceDetailsAtom,
  selectedDeviceEditDetailsAtom,
  selectedDeviceIdAtom,
  syncInProgressAtom
} from '@/pages/Administration/Devices/Devices.store';
import type { DeviceListingResponseDTO } from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';
import {
  type Organization,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

import type { DeviceDetailsDTO } from '../DevicesTable';
import type {
  DeviceDetailModel,
  SyncInprogressDTO
} from '../DevicesTable/DevicesTable.types';
import groupSourceStatusCodes from '../DevicesTable/utils/LimaSourceAggregatedStatus.utils';
import {
  deviceDialogModeAtom,
  deviceDialogOpenAtom,
  deviceNameAtom,
  deviceSizeAtom,
  deviceTypesAtom,
  hostNamePrefixAtom,
  selectedDeviceSizeIdAtom,
  selectedDeviceTypeIdAtom
} from './DeviceCreate.store';
import type {
  DeviceCreateDTO,
  DeviceDialogMode,
  DeviceSizeDTO,
  DeviceTypeDTO
} from './DeviceCreate.types';

const showSnackbar = (mode: string, enqueueSnackbar: EnqueueSnackbar) => {
  let action = '';
  switch (mode) {
    case 'Edit':
      action = 'Device  Updated';
      break;
    case 'Delete':
      action = 'Device  Deleted';
      break;
    default:
      action = 'Device  Created';
  }
  enqueueSnackbar(`${action} Successfully`, { variant: 'success' });
};

export const useDeviceDialog = () => {
  const [error, setError] = useState('');
  const [mode, setMode] = useAtom(deviceDialogModeAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useAtom(deviceDialogOpenAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);
  const [deviceId, setDeviceId] = useAtom(selectedDeviceIdAtom);

  const openDeviceDialog = (
    dialogMode: DeviceDialogMode = 'New',
    device_uid: string = ''
  ) => {
    setDeviceId(device_uid);
    setMode(dialogMode);
    setOpen(true);
  };

  const closeDeviceDialog = () => {
    setOpen(false);
  };

  const mutation = useMutation({
    mutationFn: (src: DeviceCreateDTO) => {
      const payload = src;
      switch (mode) {
        case 'Edit':
          return axiosClient.put(`/devices/${deviceId || ''}`, payload);
        case 'Delete':
          return axiosClient.delete(`/devices/${deviceId || ''}`);
        default:
          return axiosClient.post('/devices/', payload);
      }
    },
    onSuccess: (data) => {
      if (data.status === 200 || data.status === 201) {
        closeDeviceDialog();
        showSnackbar(mode, enqueueSnackbar);
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        setError(`${err?.response?.data?.detail || 'Error saving device'}`);
      } else {
        setError(`Error saving device (${err.message})`);
      }
    }
  });

  return {
    open,
    error,
    mutation,
    mode,
    setError,
    openDeviceDialog,
    closeDeviceDialog
  };
};

// DeviceSize API call
export const useFetchDeviceSize = () => {
  const [, setDeviceSizeData] = useAtom(deviceSizeAtom);
  const handleError = useHandleError();

  const deviceSizeQuery = useQuery({
    queryKey: [`device_size/`],
    staleTime: Infinity
  });

  useEffect(() => {
    if (deviceSizeQuery.isSuccess) {
      const respData: DeviceSizeDTO[] = deviceSizeQuery.data as DeviceSizeDTO[];
      setDeviceSizeData(respData);
    }
    if (deviceSizeQuery.isError) {
      setDeviceSizeData([]);
      handleError(
        deviceSizeQuery.error as AxiosError,
        'Failure to fetch device size.'
      );
    }
  }, [
    deviceSizeQuery.isSuccess,
    deviceSizeQuery.error,
    deviceSizeQuery.isError
  ]);

  return deviceSizeQuery;
};

// DeviceType API call
export const useFetchDeviceType = () => {
  const [, setDeviceTypes] = useAtom(deviceTypesAtom);

  const deviceTypeQuery = useQuery({
    queryKey: [`device_type/`],
    staleTime: Infinity
  });

  useEffect(() => {
    if (deviceTypeQuery.data) {
      const respData: DeviceTypeDTO[] = deviceTypeQuery.data as DeviceTypeDTO[];
      // Filter out EDR device types that are not needed in create device page
      setDeviceTypes(respData.filter((item) => item.id !== 4));
    }
    if (deviceTypeQuery.isError) {
      setDeviceTypes([]);
    }
  }, [deviceTypeQuery.data, deviceTypeQuery.isError]);

  return deviceTypeQuery;
};

// Fetch Device Details By Device UID
export const useFetchDeviceDetailsByDeviceUID = (
  device_uid: string,
  selected_page: string
) => {
  const [, setSelectedDeviceDetails] = useAtom(selectedDeviceDetailsAtom);
  const [devices] = useAtom(devicesAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const deviceRow = devices?.find((d) => d.device_uid === device_uid);

  let url = '';
  if (deviceRow?.device_type_id === 1) {
    // lima device
    url = `organizations/${selectedOrgData.label}/lima_device/${device_uid}`;
  } else if (deviceRow?.device_type_id) {
    // other device
    url = `devices/${device_uid}`;
  }

  const deviceDetailsQuery = useQuery<DeviceDetailsDTO>({
    queryKey: [url, selected_page],
    enabled: !!device_uid && selected_page === 'AD' && !!url
  });

  useEffect(() => {
    if (deviceDetailsQuery.isSuccess) {
      const updatedDeviceDetailData =
        deviceDetailsQuery?.data as DeviceDetailModel;
      updatedDeviceDetailData.lima_source_aggregated_status_display =
        groupSourceStatusCodes(
          updatedDeviceDetailData?.lima_source_aggregated_status ?? []
        );
      setSelectedDeviceDetails(updatedDeviceDetailData as DeviceDetailsDTO);
    }
    if (deviceDetailsQuery.isError || !deviceDetailsQuery.isSuccess) {
      setSelectedDeviceDetails({} as DeviceDetailsDTO);
    }
  }, [
    deviceDetailsQuery.data,
    deviceDetailsQuery.isError,
    deviceDetailsQuery.isSuccess
  ]);

  return deviceDetailsQuery;
};

// Fetch Device Edit By Device UID
export const useFetchDeviceEditByDeviceUID = (device_uid: string) => {
  const [, setSelectedDeviceEditDetails] = useAtom(
    selectedDeviceEditDetailsAtom
  );
  const deviceDetailsQuery = useQuery<DeviceDetailsDTO>({
    queryKey: [`devices/${device_uid}`],
    enabled: device_uid !== undefined && device_uid !== ''
  });

  // Fetching Fresh data always and avoid from cache
  useEffect(() => {
    if (
      deviceDetailsQuery.isSuccess &&
      !deviceDetailsQuery.isRefetching &&
      deviceDetailsQuery.isFetchedAfterMount
    ) {
      setSelectedDeviceEditDetails(deviceDetailsQuery.data as DeviceDetailsDTO);
    }

    if (deviceDetailsQuery.isError || !deviceDetailsQuery.isSuccess) {
      setSelectedDeviceEditDetails({} as DeviceDetailsDTO);
    }
  }, [
    deviceDetailsQuery.isSuccess,
    deviceDetailsQuery.isRefetching,
    deviceDetailsQuery.isFetchedAfterMount
  ]);

  return deviceDetailsQuery;
};

// Fetch Devices By Organization label
export const useFetchDevicesByOrg = (org: Organization) => {
  const devicesQuery = useQuery<DeviceListingResponseDTO>({
    queryKey: [`organizations/${org.label}/devices`]
  });
  return devicesQuery;
};

// Fetch Devices By Organization label
export const useFetchIsSyncInProgress = () => {
  const [, setSyncInProgress] = useAtom(syncInProgressAtom);

  const syncInProgressQuery = useQuery<SyncInprogressDTO>({
    queryKey: [`devices_sync/in_progress`]
  });

  useEffect(() => {
    if (syncInProgressQuery.isSuccess) {
      setSyncInProgress(syncInProgressQuery?.data?.status || false);
    }
    if (syncInProgressQuery.isError || !syncInProgressQuery.isSuccess) {
      setSyncInProgress(false);
    }
  }, [
    syncInProgressQuery.data,
    syncInProgressQuery.isError,
    syncInProgressQuery.isSuccess
  ]);
  return syncInProgressQuery;
};

// Custom Hook for Device Form
export const useDeviceForm = () => {
  const { reset, setValue, getValues } = useFormContext();
  const { mode } = useDeviceDialog();
  const [selectedDeviceEditDetails] = useAtom(selectedDeviceEditDetailsAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [deviceTypesData] = useAtom(deviceTypesAtom);
  const [deviceSizeData] = useAtom(deviceSizeAtom);
  const [deviceId] = useAtom(selectedDeviceIdAtom);
  const [, setHostNamePrefix] = useAtom(hostNamePrefixAtom);
  const [, setDeviceName] = useAtom(deviceNameAtom);
  const [, setSelectedDeviceTypeId] = useAtom(selectedDeviceTypeIdAtom);
  const [, setSelectedDeviceSizeId] = useAtom(selectedDeviceSizeIdAtom);

  // Fetch necessary data
  useFetchDeviceType();
  useFetchDeviceSize();
  const deviceDetailQuery = useFetchDeviceEditByDeviceUID(deviceId);

  // Update device name prefix based on device type
  const updateDeviceNamePrefix = useCallback(
    (hostName = '') => {
      const devTypeId = getValues('device_type_id');
      const devTypeShortCode =
        deviceTypesData.find((item) => item.id === devTypeId)?.short_code || '';

      if (mode === 'New') {
        const prefix = `${selectedOrgData.label}${devTypeShortCode}`;
        setValue('host_name_prefix', prefix);
        setHostNamePrefix(prefix);
      } else if (mode === 'Edit') {
        const devicePrefix = hostName.slice(0, -2);
        setValue('host_name_prefix', devicePrefix);
        setHostNamePrefix(devicePrefix);
      }
    },
    [deviceTypesData, mode, selectedOrgData]
  );

  // Handler for device type changes.
  const handleDeviceTypeChange = useCallback(
    (value: string, fieldOnChange: (value: string) => void) => {
      fieldOnChange(value); // Updates the field value in the form
      setSelectedDeviceTypeId(Number(value));
      setValue('device_size_id', 0);
      updateDeviceNamePrefix('');
    },
    []
  );

  // Handler for device size changes.
  const handleDeviceSizeChange = useCallback(
    (value: string, fieldOnChange: (value: string) => void) => {
      fieldOnChange(value);
      setSelectedDeviceSizeId(Number(value));
    },
    []
  );

  // Handler for device name changes.
  const handleDeviceNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (newValue.length <= 2) {
        setDeviceName(newValue);
      }
    },
    []
  );

  // Set default values for New and Edit mode
  useEffect(() => {
    if (
      mode === 'New' &&
      deviceTypesData?.length > 0 &&
      deviceSizeData?.length > 0
    ) {
      // Default values for New mode
      const defaultValues = {
        device_type_id: 0,
        organization_id: selectedOrgData.id,
        host_name: '',
        device_size_id: 0,
        configuration: '',
        description: '',
        status_code: STATUS_CODES.NEW
      };
      reset(defaultValues);
      setValue('device_type_id', 0);
      setDeviceName('');
    } else if (
      mode === 'Edit' &&
      selectedDeviceEditDetails?.id &&
      deviceTypesData?.length > 0 &&
      deviceSizeData?.length > 0
    ) {
      const editDetails = selectedDeviceEditDetails;

      // Extract device number and set default values
      const deviceNum = editDetails.host_name?.slice(-2) || '';
      const defaultValues = {
        device_type_id: editDetails.device_type_id || 0,
        organization_id: editDetails.organization?.id || 0,
        host_name: deviceNum,
        device_size_id: editDetails.device_size_id || 0,
        configuration: editDetails.configuration || '',
        description: editDetails.description || '',
        display_name: editDetails.display_name || ''
      };
      reset(defaultValues);
      setSelectedDeviceTypeId(editDetails.device_type_id || 0);
      setValue('device_type_id', editDetails.device_type_id || 0);
      setDeviceName(deviceNum);
      updateDeviceNamePrefix(editDetails.host_name);
    }
  }, [
    mode,
    selectedOrgData.id,
    deviceTypesData,
    deviceSizeData,
    selectedDeviceEditDetails
  ]);

  return {
    deviceDetailQuery,
    handleDeviceTypeChange,
    handleDeviceSizeChange,
    handleDeviceNameChange
  };
};
